import { Fragment, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { MenuItem, Stack, Divider, Typography, Tooltip } from '@mui/material';
// components
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import Iconify from '../../../components/Iconify';
import GuideLauncherDrawer from '../../../components/onboarding/GuideLauncherDrawer';
import CoachDrawer from '../../../components/onboarding/CoachDrawer';
import { useAdminOnboarding } from '../../../contexts/AdminOnboardingContext';
import { ANCHOR_ID } from '../../../onboarding/anchors';
import useAuth from '../../../hooks/useAuth';
import { PATH_ADMIN } from '../../../routes/paths';

// ----------------------------------------------------------------------
// Every route into help lives here, behind the one icon in the header. Nothing
// about onboarding or the AI coach floats over the app: a permanent button in
// the corner of every screen is a permanent piece of somebody's workspace.
// ----------------------------------------------------------------------

export default function HelpButtonPopover() {
  const [open, setOpen] = useState(null);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  // Only org users have a persona, and so only they have guides to launch. For
  // everyone else this is the same three-link help menu it has always been.
  const { available } = useAdminOnboarding();
  const { user, hasPermission } = useAuth();

  // The support desk is a real admin page behind a real permission, so link to
  // it only for people the route guard would actually let in. Sending anyone
  // else there would answer "I need help" with a 403.
  const canOpenSupport = hasPermission?.('deskos_support.view');
  // Training can be paid for or added to the next DeskOS bill, so it is offered
  // to the same people who see DeskOS billing. Matches the route guard.
  const canBookTraining = user?.role === 'org' && ['admin', 'accountant'].includes(user?.orgRole);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const helpOptions = [
    {
      label: 'Documentation',
      icon: 'eva:book-open-fill',
      url: 'https://deskos.net/docs'
    },
    ...(canBookTraining
      ? [{
        label: 'Get a training',
        icon: 'mdi:school-outline',
        to: PATH_ADMIN.deskosTraining.root
      }]
      : []),
    ...(canOpenSupport
      ? [{
        label: 'Create Support Request',
        icon: 'eva:question-mark-circle-fill',
        to: PATH_ADMIN.deskosSupport.root
      }]
      : []),
    {
      label: 'How to get help',
      icon: 'eva:info-fill',
      url: 'https://deskos.zohodesk.in/portal/en/kb/articles/how-to-get-get-help-for-deskos'
    }
  ];

  const lastIndex = helpOptions.length - 1;

  return (
    <>
    <Tooltip title="Help">
      <IconButtonAnimate
        onClick={handleOpen}
        data-tour={ANCHOR_ID.helpMenu}
        sx={{
          width: 40,
          height: 40,
          mr:2,
          ...(open && { bgcolor: 'action.selected' }),
        }}
      >
        <Iconify icon="eva:question-mark-circle-fill" width={20} height={20} />
      </IconButtonAnimate>
    </Tooltip>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          mt: 1.5,
          ml: 0.75,
          width: 220,
          '& .MuiMenuItem-root': { px: 1, typography: 'body2', borderRadius: 0.75 },
        }}
      >
        <Stack spacing={0.75}>
          {available && (
            <>
              <MenuItem
                onClick={() => {
                  handleClose();
                  setAskOpen(true);
                }}
              >
                <Iconify icon="mdi:robot-happy-outline" sx={{ width: 20, height: 20, mr: 2 }} />
                <Typography variant="body2">Ask AI</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  setLauncherOpen(true);
                }}
              >
                <Iconify icon="mdi:gesture-tap" sx={{ width: 20, height: 20, mr: 2 }} />
                <Typography variant="body2">Show me how</Typography>
              </MenuItem>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {helpOptions.map((option, index) => (
            <Fragment key={option.label}>
              {index === lastIndex && <Divider sx={{ my: 1 }} />}
              <MenuItem
                {...(option.to
                  ? { component: RouterLink, to: option.to }
                  : { component: 'a', href: option.url, target: '_blank', rel: 'noopener noreferrer' })}
                onClick={handleClose}
              >
                <Iconify icon={option.icon} sx={{ width: 20, height: 20, mr: 2 }} />
                <Typography variant="body2">{option.label}</Typography>
              </MenuItem>
            </Fragment>
          ))}
        </Stack>
      </MenuPopover>

      {available && (
        <>
          <CoachDrawer open={askOpen} onClose={() => setAskOpen(false)} />
          <GuideLauncherDrawer open={launcherOpen} onClose={() => setLauncherOpen(false)} />
        </>
      )}
    </>
  );
}
